import { skipToken } from "@reduxjs/toolkit/query";
import type { InterviewLoaded } from "@repo/app-types/entities/interview";
import { checkIsNextInterviewQuestionsDataExtractionFieldName } from "@repo/app-types/entities/nextInterviewQuestions";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import type { FC } from "react";
import { useDataExtractionFieldDefinitionsByJobTypeIdQuery } from "../../../api/entities/dataExtractionField.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectProjectById } from "../../projects/projectsSlice.js";
import { InterviewAssessmentRating } from "./interviewAssessmentRating.js";

interface InterviewDataExtractionSectionProps {
  interview: InterviewLoaded;
}

export const InterviewDataExtractionSection: FC<
  InterviewDataExtractionSectionProps
> = ({ interview }) => {
  const project = useAppSelector((state) =>
    selectProjectById(state, interview.project_id),
  );

  const { data: jobTypeDataExtractionFieldDefinitionIds } =
    useDataExtractionFieldDefinitionsByJobTypeIdQuery(
      project?.job_type?.id ? { jobTypeId: project.job_type.id } : skipToken,
    );

  if (!project?.job_type?.id) {
    return null;
  }

  // Find the values (if any) for the data extraction fields set on the job type.
  const dataExtractionCustomFieldValues =
    interview.custom_fields?.filter((customField) =>
      jobTypeDataExtractionFieldDefinitionIds?.includes(
        customField.custom_field_definition_id,
      ),
    ) ?? [];

  // The "Next interview questions" field is rendered separately as a card
  // below; the rest go in the standard extracted-data table.
  const nextInterviewQuestionsField = dataExtractionCustomFieldValues.find(
    (f) => checkIsNextInterviewQuestionsDataExtractionFieldName(f.field_name),
  );
  const otherDataExtractionFieldValues = dataExtractionCustomFieldValues.filter(
    (f) => !checkIsNextInterviewQuestionsDataExtractionFieldName(f.field_name),
  );

  const nextInterviewQuestions =
    interview.status !== "pending" &&
    typeof nextInterviewQuestionsField?.data.value === "string"
      ? parseNextInterviewQuestions(nextInterviewQuestionsField.data.value)
      : [];

  if (
    otherDataExtractionFieldValues.length === 0 &&
    nextInterviewQuestions.length === 0
  ) {
    return null;
  }

  return (
    <>
      {otherDataExtractionFieldValues.length > 0 && (
        <>
          <Label size={Size.MEDIUM} className="mt-4 mb-2">
            Extracted data
          </Label>
          <div className="mt-4 overflow-hidden border border-border-light sm:rounded-lg">
            <table className="min-h-full w-full table-fixed divide-y divide-border-light">
              <thead className="group bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="w-[25%] px-4 py-2 text-left text-xs font-medium text-muted-foreground"
                  >
                    Field name
                  </th>
                  <th
                    scope="col"
                    className="w-[15%] px-4 py-2 text-left text-xs font-medium text-muted-foreground"
                  >
                    Value
                  </th>
                  <th
                    scope="col"
                    className="w-[60%] px-4 py-2 text-left text-xs font-medium text-muted-foreground"
                  >
                    Comments
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-light bg-white">
                {otherDataExtractionFieldValues.map((field) => (
                  <tr key={field.field_name} className={`group relative`}>
                    <td className={`px-4 py-2 text-sm text-foreground`}>
                      {field.display_name}
                    </td>
                    <td className="px-4 py-2 text-sm text-foreground">
                      {interview.status === "pending" ? (
                        <span className="text-gray-400">--</span>
                      ) : (
                        <InterviewAssessmentRating
                          rating={field.data.value}
                          fieldName={field.field_name}
                        />
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-foreground">
                      {field.data.display_value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {nextInterviewQuestions.length > 0 && (
        <>
          <Label size={Size.MEDIUM} className="mt-4 mb-2">
            Next interview tips
          </Label>
          <div className="mt-4 overflow-hidden border border-border-light p-4 sm:rounded-lg">
            <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
              {nextInterviewQuestions.map((question, index) => (
                <li key={index}>{question}</li>
              ))}
            </ul>
          </div>
        </>
      )}
    </>
  );
};

/**
 * The LLM is prompted to return one bullet per line, each starting with "- ",
 * but in practice it sometimes uses numbered lists or other markers. Strip
 * any leading bullet/number marker so we can render with our own list styles.
 */
function parseNextInterviewQuestions(value: string): string[] {
  return value
    .split("\n")
    .map((line) =>
      line
        .trim()
        .replace(/^(?:[-*•]|\d+[.)])\s+/, "")
        .trim(),
    )
    .filter((line) => line.length > 0);
}
